import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LineChart, BarChart3, TrendingUp, TrendingDown, RefreshCw, Zap, Shield, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  LineChart as RechartsLine,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function GlabsStatCard({ label, value, sub, color, icon: Icon }) {
  return (
    <div className="solar-card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-[hsl(215,16%,56%)]">{label}</span>
        {Icon && <Icon className="w-4 h-4" style={{ color }} />}
      </div>
      <p className="text-2xl font-bold font-mono text-white">{value}</p>
      {sub && <p className="text-xs text-[hsl(215,16%,56%)] mt-1">{sub}</p>}
    </div>
  );
}

function SupplyBar({ sold, available, total }) {
  const soldPct = total > 0 ? Math.min((sold / total) * 100, 100) : 0;
  const availPct = total > 0 ? Math.min((available / total) * 100, 100) : 0;
  return (
    <div className="space-y-2">
      <div className="h-3 w-full bg-[hsl(220,16%,12%)] rounded-full overflow-hidden flex">
        <div className="h-full bg-[#E8520F]" style={{ width: `${soldPct}%` }} title="Sold" />
        <div className="h-full bg-[#10B981]" style={{ width: `${availPct - soldPct}%` }} title="Available in LP" />
        <div className="h-full bg-[hsl(220,16%,22%)] flex-1" title="Reserve" />
      </div>
      <div className="flex items-center gap-4 text-[10px] text-[hsl(215,16%,56%)]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E8520F] inline-block" /> Sold: {sold?.toLocaleString()}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981] inline-block" /> LP: {available?.toLocaleString()}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[hsl(220,16%,22%)] inline-block" /> Reserve: {(total - available)?.toLocaleString()}</span>
      </div>
    </div>
  );
}

export default function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/market`, { withCredentials: true }),
        axios.get(`${API_URL}/api/analytics/history`, { withCredentials: true })
      ]);
      setAnalytics(analyticsRes.data);
      setHistory(historyRes.data.history);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const glabs = analytics?.glabs;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-white flex items-center gap-2">
            <LineChart className="w-7 h-7 text-[#10B981]" />
            Market Analytics
          </h1>
          <p className="text-[hsl(215,16%,56%)]">Real-time price, liquidity, and volume metrics</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}
          className="border-[hsl(220,16%,18%)] text-white bg-transparent hover:bg-[hsl(220,16%,16%)]"
          data-testid="refresh-analytics-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* ── GLabs Token Section ── */}
      {glabs && (
        <Card className="border-[#D4A017]/30 bg-gradient-to-br from-[#D4A017]/5 to-transparent shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#D4A017]" />
              GLabs Token — Live Market Data
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/15 text-[#10B981] font-mono">INTERNAL</span>
            </CardTitle>
            <CardDescription>{glabs.note}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <GlabsStatCard
                label="Credit Price"
                value={`$${glabs.price_per_credit_usdt?.toFixed(4)}`}
                sub="USDT per credit"
                color="#D4A017"
                icon={Zap}
              />
              <GlabsStatCard
                label="Total Supply"
                value={`${(glabs.total_supply / 1e9).toFixed(1)}B`}
                sub="1 billion total"
                color="#6366F1"
                icon={Activity}
              />
              <GlabsStatCard
                label="LP Available"
                value={`${(glabs.lp_available / 1e6).toFixed(0)}M`}
                sub="600M in LP pool"
                color="#10B981"
                icon={Shield}
              />
              <GlabsStatCard
                label="Credits Sold"
                value={glabs.sold?.toLocaleString()}
                sub={`${glabs.sold_pct}% of LP`}
                color="#E8520F"
                icon={TrendingUp}
              />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs text-[hsl(215,16%,56%)]">
                <span>Supply Distribution</span>
                <span className="font-mono">{glabs.sold_pct}% sold of LP</span>
              </div>
              <SupplyBar sold={glabs.sold} available={glabs.lp_available} total={glabs.total_supply} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live Ticker */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="solar-card p-4" data-testid="analytics-price-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[hsl(215,16%,56%)]">Current Price</span>
            {analytics?.price_change_24h >= 0
              ? <TrendingUp className="w-4 h-4 text-[#10B981]" />
              : <TrendingDown className="w-4 h-4 text-[#EF4444]" />}
          </div>
          <p className="text-2xl font-bold font-mono text-white">${analytics?.price?.toFixed(6) || '0.000000'}</p>
          <p className={`text-sm ${analytics?.price_change_24h >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
            {analytics?.price_change_24h >= 0 ? '+' : ''}{analytics?.price_change_24h?.toFixed(2) || '0.00'}% (24h)
          </p>
        </div>
        <div className="solar-card p-4" data-testid="analytics-liquidity-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[hsl(215,16%,56%)]">Total Liquidity</span>
            <div className="w-2 h-2 rounded-full bg-[#00CED1] animate-pulse" />
          </div>
          <p className="text-2xl font-bold font-mono text-white">${analytics?.liquidity?.toLocaleString() || '0'}</p>
          <p className="text-sm text-[hsl(215,16%,56%)]">Across all pools</p>
        </div>
        <div className="solar-card p-4" data-testid="analytics-volume-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[hsl(215,16%,56%)]">24h Volume</span>
            <BarChart3 className="w-4 h-4 text-[#E8520F]" />
          </div>
          <p className="text-2xl font-bold font-mono text-white">${analytics?.volume_24h?.toLocaleString() || '0'}</p>
          <p className="text-sm text-[hsl(215,16%,56%)]">Total traded</p>
        </div>
        <div className="solar-card p-4" data-testid="analytics-bot-volume-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[hsl(215,16%,56%)]">Bot Volume</span>
            <div className="px-2 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B] rounded text-xs font-medium">LIVE</div>
          </div>
          <p className="text-2xl font-bold font-mono text-white">${analytics?.bot_volume?.toLocaleString() || '0'}</p>
          <p className="text-sm text-[hsl(215,16%,56%)]">Bot-attributed</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardHeader>
            <CardTitle className="text-white font-['Outfit']">Price History (24h)</CardTitle>
            <CardDescription>Hourly price movements</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsLine data={history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,16%,18%)" />
                  <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${v.toFixed(4)}`} domain={['dataMin - 0.0001', 'dataMax + 0.0001']} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(220,18%,11%)', border: '1px solid hsl(220,16%,18%)', borderRadius: '8px', color: '#fff' }}
                    formatter={(value) => [`$${value.toFixed(6)}`, 'Price']}
                    labelFormatter={(h) => `Hour: ${h}:00`} />
                  <Line type="monotone" dataKey="price" stroke="#E8520F" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#E8520F' }} />
                </RechartsLine>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardHeader>
            <CardTitle className="text-white font-['Outfit']">Volume & Liquidity (24h)</CardTitle>
            <CardDescription>Hourly trading activity</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,16%,18%)" />
                  <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} stroke="#64748B" fontSize={12} />
                  <YAxis yAxisId="left" stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(220,18%,11%)', border: '1px solid hsl(220,16%,18%)', borderRadius: '8px', color: '#fff' }}
                    formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                    labelFormatter={(h) => `Hour: ${h}:00`} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="volume" fill="#00CED1" name="Volume" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="liquidity" fill="#10B981" name="Liquidity" radius={[4, 4, 0, 0]} opacity={0.6} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hourly table */}
      <Card className="border-[hsl(220,16%,18%)] shadow-none">
        <CardHeader>
          <CardTitle className="text-white font-['Outfit']">Hourly Breakdown</CardTitle>
          <CardDescription>Detailed metrics for each hour</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[hsl(220,16%,18%)]">
                  <th className="text-left py-3 px-4 text-[hsl(215,16%,56%)] font-medium">Hour</th>
                  <th className="text-right py-3 px-4 text-[hsl(215,16%,56%)] font-medium">Price</th>
                  <th className="text-right py-3 px-4 text-[hsl(215,16%,56%)] font-medium">Volume</th>
                  <th className="text-right py-3 px-4 text-[hsl(215,16%,56%)] font-medium">Liquidity</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 12).map((row, idx) => (
                  <tr key={idx} className="border-b border-[hsl(220,16%,18%)] hover:bg-[hsl(220,16%,14%)]">
                    <td className="py-3 px-4 text-white">{row.hour}:00</td>
                    <td className="py-3 px-4 text-right font-mono text-white">${row.price.toFixed(6)}</td>
                    <td className="py-3 px-4 text-right font-mono text-[#00CED1]">${row.volume.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right font-mono text-[#10B981]">${row.liquidity.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
